import { Col, Form, Row, Tabs, Button } from "antd";
import type { FormInstance } from "antd";
import type { SelectProps } from "antd/lib/select";
import type { CommunicationSetupIssue } from "./communicationSetupChecks";
import { COMMUNICATION_SECTIONS } from "./communicationSetupChecks";
import CommunicationSetupAlert from "./CommunicationSetupAlert";
import DocumentTemplatesPanel from "./DocumentTemplatesPanel";
import EmailTemplatesPanel from "./EmailTemplatesPanel";
import SmsTemplatesPanel from "./SmsTemplatesPanel";
import DocumentRulesPanel from "./DocumentRulesPanel";
import EmailSettingsPanel from "./EmailSettingsPanel";
import SmsCredentialsPanel from "./SmsCredentialsPanel";
import BulkSmsPanel from "./BulkSmsPanel";

import "../../styles/GroupManagement.css";

type TextAreaRefs = {
  [key: string]: React.RefObject<any>;
};

export type GroupCommunicationTabProps = {
  mode: "tenant" | "admin";
  groupId: string;
  group: any;
  documents: any[];
  communicationDocuments: any[];
  form: FormInstance;
  formData: any;
  formDisabled: {
    docs: boolean;
    sms?: boolean;
    basic: boolean;
  };
  loading: boolean;
  layout: object;
  tailLayout: object;
  documentOptions: SelectProps["options"];
  textAreaRefs: TextAreaRefs;
  communicationSubTab: string;
  setCommunicationSubTab: (key: string) => void;
  setupIssues: CommunicationSetupIssue[];
  onNavigateToSection: (section: string) => void;
  onDeleteDocument: (id: string) => void;
  onSmsConfigSubmit: (values: any) => void;
  onSmsCredentialsSubmit: (values: any) => void;
  onEmailSettingsSubmit: (values: any) => void;
  onSendBulkSms: (values: any) => void;
  onToggleSmsConfigEdit: () => void;
  onToggleSmsCredentialsEdit?: () => void;
  onToggleEmailSettingsEdit: () => void;
  onAddVariable: () => void;
  onSelectTextArea: (name: string) => void;
  onSmsEnabledChange: (checked: boolean) => void;
  onEmailNotificationChange: (checked: boolean) => void;
  onChangeDocument: (value: any) => void;
  onFormDirty: () => void;
  onGenerateDocuments?: () => void;
  messageTypeOptions?: SelectProps["options"];
  emailTemplateOptions?: SelectProps["options"];
};

const GroupCommunicationTab: React.FC<GroupCommunicationTabProps> = ({
  mode,
  groupId,
  group,
  documents,
  communicationDocuments,
  form,
  formData,
  formDisabled,
  loading,
  layout,
  tailLayout,
  documentOptions,
  textAreaRefs,
  communicationSubTab,
  setCommunicationSubTab,
  setupIssues,
  onNavigateToSection,
  onDeleteDocument,
  onSmsConfigSubmit,
  onSmsCredentialsSubmit,
  onEmailSettingsSubmit,
  onSendBulkSms,
  onToggleSmsConfigEdit,
  onToggleSmsCredentialsEdit,
  onToggleEmailSettingsEdit,
  onAddVariable,
  onSelectTextArea,
  onSmsEnabledChange,
  onEmailNotificationChange,
  onChangeDocument,
  onFormDirty,
  onGenerateDocuments,
  messageTypeOptions,
  emailTemplateOptions = [],
}) => {
  const smsEnabled = Boolean(formData?.SmsEnabled ?? group.SmsEnabled);
  const smsApiKey = String(
    formData?.SmsApiKey ?? group?.SmsApiKey ?? ""
  ).trim();
  const smsApiSecret = String(
    formData?.SmsApiSecret ?? group?.SmsApiSecret ?? ""
  ).trim();
  const hasSmsCredentials = Boolean(smsApiKey && smsApiSecret);

  const subTabItems = [
    {
      key: COMMUNICATION_SECTIONS.documentTemplates,
      label: "Policy & Application Documents",
      children: (
        <DocumentTemplatesPanel
          mode={mode}
          groupId={groupId}
          documents={documents}
          handleDeleteDocument={onDeleteDocument}
          onGenerateDocuments={onGenerateDocuments}
        />
      ),
    },
    {
      key: COMMUNICATION_SECTIONS.emailTemplates,
      label: "Email Templates",
      children: (
        <EmailTemplatesPanel
          mode={mode}
          groupId={groupId}
          communicationDocuments={communicationDocuments}
        />
      ),
    },
    {
      key: COMMUNICATION_SECTIONS.smsTemplates,
      label: "SMS Templates",
      children: (
        <Form
          {...layout}
          form={form}
          initialValues={formData}
          disabled={formDisabled.docs}
          onFinish={onSmsConfigSubmit}
          onValuesChange={onFormDirty}
        >
          <SmsTemplatesPanel
            embedded
            smsEnabled={smsEnabled}
            textAreaRefs={textAreaRefs}
            showEditControls
            onToggleEdit={onToggleSmsConfigEdit}
            onAddVariable={onAddVariable}
            onSmsEnabledChange={onSmsEnabledChange}
            onSelectTextArea={onSelectTextArea}
          />
          {smsEnabled && !formDisabled.docs && (
            <div className="card-content" style={{ marginTop: 16 }}>
              <Button block size="large" htmlType="submit" loading={loading}>
                Submit
              </Button>
            </div>
          )}
        </Form>
      ),
    },
    {
      key: COMMUNICATION_SECTIONS.documentRules,
      label: "Document Assignment & Rules",
      children: (
        <Form
          {...layout}
          form={form}
          initialValues={formData}
          disabled={formDisabled.docs}
          onFinish={onSmsConfigSubmit}
          onValuesChange={onFormDirty}
        >
          <DocumentRulesPanel
            embedded
            smsEnabled={smsEnabled}
            documentOptions={documentOptions}
            showEditControls
            onToggleEdit={onToggleSmsConfigEdit}
            onChangeDocument={onChangeDocument}
            formDisabled={formDisabled.docs}
            showSubmit
            loading={loading}
            tailLayout={tailLayout}
          />
        </Form>
      ),
    },
    {
      key: COMMUNICATION_SECTIONS.emailSettings,
      label: "Email Settings",
      children: (
        <EmailSettingsPanel
          form={form}
          formData={formData}
          formDisabled={formDisabled.basic}
          loading={loading}
          layout={layout}
          tailLayout={tailLayout}
          emailTemplateOptions={emailTemplateOptions}
          onToggleEdit={onToggleEmailSettingsEdit}
          onEmailNotificationChange={onEmailNotificationChange}
          onValuesChange={onFormDirty}
          onSubmit={onEmailSettingsSubmit}
        />
      ),
    },
    {
      key: COMMUNICATION_SECTIONS.smsCredentials,
      label: "SMS Credentials",
      children: (
        <SmsCredentialsPanel
          mode={mode}
          group={group}
          form={form}
          formData={formData}
          formDisabled={Boolean(formDisabled.sms)}
          layout={layout}
          tailLayout={tailLayout}
          onToggleEdit={onToggleSmsCredentialsEdit}
          onValuesChange={onFormDirty}
          onSubmit={onSmsCredentialsSubmit}
        />
      ),
    },
  ];

  if (mode === "tenant" && messageTypeOptions) {
    subTabItems.push({
      key: COMMUNICATION_SECTIONS.bulkSms,
      label: "Bulk SMS",
      children: (
        <BulkSmsPanel
          layout={layout}
          tailLayout={tailLayout}
          loading={loading}
          messageTypeOptions={messageTypeOptions}
          smsEnabled={smsEnabled}
          hasSmsCredentials={hasSmsCredentials}
          onNavigateToCredentials={() =>
            onNavigateToSection(COMMUNICATION_SECTIONS.smsCredentials)
          }
          onNavigateToSmsTemplates={() =>
            onNavigateToSection(COMMUNICATION_SECTIONS.smsTemplates)
          }
          onSend={onSendBulkSms}
        />
      ),
    });
  }

  return (
    <div className="content-border">
      <CommunicationSetupAlert
        issues={setupIssues}
        onNavigate={onNavigateToSection}
        style={{ marginTop: 0 }}
      />
      <Row style={{ paddingTop: setupIssues.length ? 0 : 24 }}>
        <Col span={24}>
          <Tabs
            type="card"
            className="group-communication-tabs"
            activeKey={communicationSubTab}
            onChange={setCommunicationSubTab}
            destroyInactiveTabPane
            items={subTabItems}
          />
        </Col>
      </Row>
    </div>
  );
};

export default GroupCommunicationTab;
